<script lang="ts">
	import { browser } from '$app/environment';
	import dayjs from 'dayjs';
	import timezone from 'dayjs/plugin/timezone';
	import utc from 'dayjs/plugin/utc';
	import { createChart, LineStyle, type UTCTimestamp } from 'lightweight-charts';
	import { map } from 'lodash-es';
	import StandardDeviationLegend from './StandardDeviationLegend.svelte';

	dayjs.extend(utc);
	dayjs.extend(timezone);

	let el: HTMLElement | undefined = $state();

	interface Props {
		data: DividendYieldHistory;
		mean: number;
		sd: number;
	}

	let { data, mean, sd }: Props = $props();

	$effect(() => {
		if (browser && el) {
			el.innerHTML = '';

			const chart = createChart(el, {
				autoSize: true,
				grid: {
					vertLines: {
						visible: false
					},
					horzLines: {
						visible: false
					}
				},
				timeScale: {
					timeVisible: true,
					fixLeftEdge: true,
					fixRightEdge: true
				},
				layout: {
					attributionLogo: false
				}
			});
			const lineSeries = chart.addLineSeries({
				priceLineVisible: false,
				lastValueVisible: false
			});

			lineSeries.setData(
				map(data, ({ date, dividendYield }) => ({
					time: dayjs(date).tz('utc', true).unix() as UTCTimestamp,
					value: dividendYield
				}))
			);

			lineSeries.createPriceLine({
				price: data[data.length - 1].dividendYield,
				color: '#2196f3',
				lineWidth: 1,
				lineStyle: LineStyle.Solid,
				lineVisible: false
			});

			lineSeries.createPriceLine({
				price: mean + sd,
				color: '#00873C',
				lineWidth: 1,
				lineStyle: LineStyle.Solid
			});

			lineSeries.createPriceLine({
				price: mean,
				color: '#7C7C7C',
				lineWidth: 1,
				lineStyle: LineStyle.Solid
			});

			lineSeries.createPriceLine({
				price: mean - sd,
				color: '#EB0F29',
				lineWidth: 1,
				lineStyle: LineStyle.Solid
			});

			chart.timeScale().fitContent();
			chart.applyOptions({
				localization: {
					priceFormatter: (price: number) => {
						return price.toLocaleString('default', {
							style: 'percent',
							minimumFractionDigits: 1,
							maximumFractionDigits: 1
						});
					}
				}
			});
		}
	});
</script>

<p class="card-bold mb-5">Historical Dividend Yield</p>
<StandardDeviationLegend />
<figure bind:this={el} class="-mr-4 aspect-3/2 w-full"></figure>
